/**
 * LoginPage — Handles sign-in, sign-up, email verification, and password change.
 *
 * Modes:
 *   - "signin"  → email + password form
 *   - "signup"  → first name, last name, preferred name, email, password
 *   - "verify"  → email verification code (after signup)
 *   - "newpw"   → set new password (FORCE_CHANGE_PASSWORD from AdminCreateUser)
 *
 * Rendered for both /login and /signup routes — mode is set based on URL.
 */

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { BRAND } from '../../config/branding';

type Mode = 'signin' | 'signup' | 'verify' | 'newpw';

const LoginPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    signIn, signUp, confirmSignUp, completeNewPassword,
    newPasswordRequired, isAuthenticated,
  } = useAuth();

  const [mode, setMode] = useState<Mode>(location.pathname === '/signup' ? 'signup' : 'signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [preferredName, setPreferredName] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Switch to newpw mode when FORCE_CHANGE_PASSWORD triggers
  useEffect(() => {
    if (newPasswordRequired) {
      setMode('newpw');
      setError('');
    }
  }, [newPasswordRequired]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/app', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;
    setError('');
    setSubmitting(true);
    try {
      await signIn(email, password);
      // If newPasswordRequired is set, useEffect will switch to 'newpw' mode
      // Otherwise, useEffect will redirect to /app
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Sign in failed';
      if (msg.includes('Incorrect username or password') || msg.includes('UserNotFoundException')) {
        setError('Incorrect email or password.');
      } else if (msg.includes('User is not confirmed')) {
        setMode('verify');
        setError('Please verify your email first.');
      } else {
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await signUp({ email, password, firstName, lastName, preferredName });
      setMode('verify');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Sign up failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await confirmSignUp(email, verifyCode);
      // Auto sign-in after verification
      await signIn(email, password);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Verification failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await completeNewPassword(newPassword);
      // useEffect will redirect to /app
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Password change failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card" style={{ maxWidth: 440 }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <h2 style={{ margin: '0 0 8px', color: 'var(--color-text)' }}>
            {mode === 'signin' && `Sign in to ${BRAND.name}`}
            {mode === 'signup' && `Create your ${BRAND.name} account`}
            {mode === 'verify' && 'Verify your email'}
            {mode === 'newpw' && 'Set your new password'}
          </h2>
          <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>
            {mode === 'signin' && 'Plan meals with your family.'}
            {mode === 'signup' && 'Start your 14-day free trial.'}
            {mode === 'verify' && `We sent a code to ${email}`}
            {mode === 'newpw' && 'Choose a password you\'ll remember.'}
          </p>
        </div>

        {/* Error banner */}
        {error && (
          <div style={{
            background: '#FEE2E2', color: '#991B1B',
            padding: '10px 14px', borderRadius: 8, marginBottom: 16,
            fontSize: 14, lineHeight: 1.4,
          }}>
            {error}
          </div>
        )}

        {/* ---- SIGN IN ---- */}
        {mode === 'signin' && (
          <form onSubmit={handleSignIn}>
            <div style={{ marginBottom: 16 }}>
              <label className="form-label">Email</label>
              <input
                className="form-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                autoComplete="email"
                autoFocus
                required
              />
            </div>
            <div style={{ marginBottom: 24 }}>
              <label className="form-label">Password</label>
              <input
                className="form-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                required
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
              style={{ width: '100%', padding: '12px', fontWeight: 600 }}
            >
              {submitting ? 'Signing in...' : 'Sign In'}
            </button>
            <p style={{ textAlign: 'center', marginTop: 16, fontSize: 14, color: 'var(--color-text-muted)' }}>
              Don't have an account?{' '}
              <Link
                to="/signup"
                onClick={(e) => { e.preventDefault(); setMode('signup'); setError(''); }}
                style={{ color: 'var(--color-primary, #2D6A4F)', fontWeight: 600 }}
              >
                Sign up
              </Link>
            </p>
          </form>
        )}

        {/* ---- SIGN UP ---- */}
        {mode === 'signup' && (
          <form onSubmit={handleSignUp}>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <label className="form-label">First Name</label>
                <input
                  className="form-input"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name"
                  autoFocus
                  required
                />
              </div>
              <div style={{ flex: 1 }}>
                <label className="form-label">Last Name</label>
                <input
                  className="form-input"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name"
                  required
                />
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label className="form-label">Preferred Name <span style={{ fontWeight: 400, color: 'var(--color-text-muted)' }}>(optional)</span></label>
              <input
                className="form-input"
                value={preferredName}
                onChange={(e) => setPreferredName(e.target.value)}
                placeholder={firstName || 'What should we call you?'}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label className="form-label">Email</label>
              <input
                className="form-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                autoComplete="email"
                required
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label className="form-label">Password</label>
              <input
                className="form-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                autoComplete="new-password"
                required
                minLength={8}
              />
            </div>
            <div style={{ marginBottom: 24 }}>
              <label className="form-label">Confirm Password</label>
              <input
                className="form-input"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Type it again"
                autoComplete="new-password"
                required
                minLength={8}
              />
            </div>
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: 8,
              marginBottom: 24, fontSize: 14, color: 'var(--color-text-secondary, #4B5563)',
              cursor: 'pointer', lineHeight: 1.4,
            }}>
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                style={{ marginTop: 3, accentColor: 'var(--color-primary, #2D6A4F)' }}
                required
              />
              <span>
                I agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer"
                  style={{ color: 'var(--color-primary, #2D6A4F)', fontWeight: 600 }}>
                  Terms of Service
                </a>{' '}and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer"
                  style={{ color: 'var(--color-primary, #2D6A4F)', fontWeight: 600 }}>
                  Privacy Policy
                </a>
              </span>
            </label>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting || !agreedToTerms}
              style={{ width: '100%', padding: '12px', fontWeight: 600 }}
            >
              {submitting ? 'Creating account...' : 'Create Account'}
            </button>
            <p style={{ textAlign: 'center', marginTop: 16, fontSize: 14, color: 'var(--color-text-muted)' }}>
              Already have an account?{' '}
              <Link
                to="/login"
                onClick={(e) => { e.preventDefault(); setMode('signin'); setError(''); }}
                style={{ color: 'var(--color-primary, #2D6A4F)', fontWeight: 600 }}
              >
                Sign in
              </Link>
            </p>
          </form>
        )}

        {/* ---- VERIFY EMAIL ---- */}
        {mode === 'verify' && (
          <form onSubmit={handleVerify}>
            <div style={{ marginBottom: 24 }}>
              <label className="form-label">Verification Code</label>
              <input
                className="form-input"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value)}
                placeholder="Enter the 6-digit code"
                autoFocus
                required
                style={{ textAlign: 'center', fontSize: 20, letterSpacing: 8 }}
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
              style={{ width: '100%', padding: '12px', fontWeight: 600 }}
            >
              {submitting ? 'Verifying...' : 'Verify & Sign In'}
            </button>
            <p style={{ textAlign: 'center', marginTop: 16, fontSize: 14, color: 'var(--color-text-muted)' }}>
              Didn't get the code?{' '}
              <button
                type="button"
                onClick={() => { setMode('signup'); setError(''); }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--color-primary, #2D6A4F)', fontWeight: 600, fontSize: 14,
                }}
              >
                Go back
              </button>
            </p>
          </form>
        )}

        {/* ---- NEW PASSWORD (FORCE_CHANGE_PASSWORD) ---- */}
        {mode === 'newpw' && (
          <form onSubmit={handleNewPassword}>
            <div style={{ marginBottom: 16 }}>
              <label className="form-label">New Password</label>
              <input
                className="form-input"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
                autoComplete="new-password"
                autoFocus
                required
                minLength={8}
              />
            </div>
            <div style={{ marginBottom: 24 }}>
              <label className="form-label">Confirm Password</label>
              <input
                className="form-input"
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                placeholder="Type it again"
                autoComplete="new-password"
                required
                minLength={8}
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
              style={{ width: '100%', padding: '12px', fontWeight: 600 }}
            >
              {submitting ? 'Setting password...' : 'Set Password & Sign In'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default LoginPage;